/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { array, number, bool } from 'prop-types';
import { ButtonStyled } from '@nike/nike-design-system-components';
import { useHistory } from 'react-router-dom';

/**
 * @name UnderlineNavigation
 * @description add styled navigation
 * @param {array} list to render link or button accordingly
 * @param initialActiveTab the initial tab to have an underline
 * @param reset whether the active tab should be reset to the initial active tab
 * @example
 * <UnderlineNavigation list=[{ href: '/', title: 'somewhere' }, { onClick: () => { ... }, title: 'doSomething' }] />
 */

const UnderlineNavigation = ({ list, initialActiveTab, reset }) => {
  const [activeElement, setActiveElement] = useState(initialActiveTab);
  const history = useHistory();

  useEffect(() => {
    if (reset) {
      setActiveElement(initialActiveTab);
    }
  });

  return (
    <nav style={{ height: '100%' }}>
      <ul
        css={css`
          & > li {
            display: inline-block;
            &:nth-of-type(n + 2) {
              margin-left: 18px;
            }
          }
        `}
      >
        {list.map(({ onClick, title = '', href }, index) => (
          <li id={index} key={title}>
            <ButtonStyled
              id={index}
              // textUnderline style is being overridden by Podium text styles in ButtonStyled, which makes no sense
              appearance={activeElement === index ? 'textUnderline' : 'text'}
              onClick={event => {
                onClick?.(event);
                setActiveElement(index);
                history.push(href);
              }}
              Component='a'
            >
              <span
                {...(activeElement === index
                  ? { style: { textDecoration: 'underline', textUnderlineOffset: '4px' } }
                  : {})}
              >
                {title}
              </span>
            </ButtonStyled>
          </li>
        ))}
      </ul>
    </nav>
  );
};

UnderlineNavigation.defaultProps = {
  list: null,
  initialActiveTab: 0,
  reset: false
};

UnderlineNavigation.propTypes = {
  list: array,
  initialActiveTab: number,
  reset: bool
};

export default UnderlineNavigation;
